import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { OfflineService, NetworkStatus } from '../../services/offline.service';

@Component({
  selector: 'app-offline-indicator',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './offline-indicator.component.html',
  styleUrl: './offline-indicator.component.css'
})
export class OfflineIndicatorComponent implements OnInit, OnDestroy {
  @Input() position: 'top' | 'bottom' | 'fixed' = 'top';
  @Input() showNetworkInfo: boolean = false;
  @Input() showSyncStatus: boolean = true;
  @Input() compact: boolean = false;

  private destroy$ = new Subject<void>();
  
  networkStatus: NetworkStatus | null = null;
  syncInProgress = false;
  updateAvailable = false;
  offlineDataCount = 0;
  syncQueueCount = 0;

  constructor(private offlineService: OfflineService) {}

  ngOnInit(): void {
    // Subscribe to network status
    this.offlineService.networkStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.networkStatus = status;
        this.updateOfflineStats();
      });

    // Subscribe to sync status
    this.offlineService.syncInProgress$
      .pipe(takeUntil(this.destroy$))
      .subscribe(inProgress => {
        this.syncInProgress = inProgress;
      });

    // Subscribe to update availability
    this.offlineService.updateAvailable$
      .pipe(takeUntil(this.destroy$))
      .subscribe(available => {
        this.updateAvailable = available;
      });

    // Update stats periodically
    setInterval(() => {
      this.updateOfflineStats();
    }, 5000);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateOfflineStats(): void {
    this.offlineDataCount = this.offlineService.getOfflineDataCount();
    this.syncQueueCount = this.offlineService.getSyncQueueCount();
  }

  // User actions
  async triggerSync(): Promise<void> {
    if (this.networkStatus?.isOnline && !this.syncInProgress) {
      await this.offlineService.syncOfflineData();
    }
  }

  async activateUpdate(): Promise<void> {
    if (this.updateAvailable) {
      try {
        await this.offlineService.activateUpdate();
      } catch (error) {
        console.error('Failed to activate update:', error);
      }
    }
  }

  // Helper methods for template
  getConnectionIcon(): string {
    if (!this.networkStatus) return 'fas fa-question-circle';
    
    if (!this.networkStatus.isOnline) {
      return 'fas fa-wifi-slash text-danger';
    }
    
    if (this.networkStatus.isSlowConnection) {
      return 'fas fa-wifi text-warning';
    }
    
    return 'fas fa-wifi text-success';
  }

  getConnectionText(): string {
    if (!this.networkStatus) return 'Unknown';
    
    if (!this.networkStatus.isOnline) {
      return 'Offline';
    }
    
    if (this.networkStatus.isSlowConnection) {
      return 'Slow Connection';
    }
    
    return 'Online';
  }

  getConnectionClass(): string {
    if (!this.networkStatus) return 'border-secondary';
    
    if (!this.networkStatus.isOnline) {
      return 'border-danger bg-danger bg-opacity-10';
    }
    
    if (this.networkStatus.isSlowConnection) {
      return 'border-warning bg-warning bg-opacity-10';
    }
    
    return 'border-success bg-success bg-opacity-10';
  }

  getSyncIcon(): string {
    if (this.syncInProgress) {
      return 'fas fa-sync fa-spin text-info';
    }
    
    if (this.syncQueueCount > 0) {
      return 'fas fa-clock text-warning';
    }
    
    return 'fas fa-check text-success';
  }

  getSyncText(): string {
    if (this.syncInProgress) {
      return 'Syncing...';
    }
    
    if (this.syncQueueCount > 0) {
      return `${this.syncQueueCount} pending`;
    }
    
    return 'Synced';
  }

  formatConnectionSpeed(): string {
    if (!this.networkStatus) return 'N/A';
    
    const { downlink, rtt } = this.networkStatus;
    if (downlink > 0) {
      return `${downlink} Mbps`;
    }
    
    if (rtt > 0) {
      return `${rtt}ms RTT`;
    }
    
    return 'Unknown speed';
  }

  formatConnectionType(): string {
    if (!this.networkStatus) return 'Unknown';
    
    const type = this.networkStatus.effectiveType;
    const typeMap: { [key: string]: string } = {
      'slow-2g': 'Slow 2G',
      '2g': '2G',
      '3g': '3G',
      '4g': '4G'
    };
    
    return typeMap[type] || type.toUpperCase();
  }
}